<template>
    <div @click="showModal()" class="form-control e-select-dialog" :class="{'auto-height': autoHeight || isExpandedSelection}">
        <div class="selection mr-1">
            <template v-if="isEmptySelected">
                <slot name="placeholder">
                    <span>{{emptyLabel|tr}}</span>
                </slot>
            </template>
            <template v-else-if="selection && selection.length > 0">
                <slot name="selection" v-bind:selection="selection">
                    <template v-if="multiple">
                        <slot name="multi-selection" v-bind:selection="selection" v-bind:options="selectionOptionsDetails">
                            <template v-if="selectionOptionsDetails.length === 0">
                                <span>{{ selection.length }}</span>
                                <span class="ml-1" v-tr>selected {{itemName|tr}}|{{itemName|tr}} sélectionnés</span>
                            </template>
                            <div v-else-if="isExpandedSelection" class="selection-badges">
                                <b-badge v-for="item in selectionOptionsDetails" :key="`selection-${item[trackBy]}`" :variant="badgeVariant">
                                    <slot name="badge" v-bind:option="item">{{ getItemLabel(item) }}</slot>
                                </b-badge>
                            </div>
                            <div v-else class="selection-badges">
                                <b-badge class="first-selection" :variant="badgeVariant">
                                    <slot name="badge" v-bind:option="selectionOptionsDetails[0]">{{ getItemLabel(selectionOptionsDetails[0]) }}</slot>
                                </b-badge>
                                <b-badge v-if="selectionOptionsDetails.length > 1" class="other-selection-count" :variant="badgeVariant" :title="otherSelectionLabels">
                                    +{{ selectionOptionsDetails.length - 1 }}
                                </b-badge>
                            </div>
                        </slot>
                    </template>
                    <template v-else-if="singleOptionSelected">
                        <slot name="single-selection" v-bind:selection="selection" v-bind:option="singleOptionSelected">
                            <span>{{ getItemLabel(singleOptionSelected) }}</span>
                        </slot>
                    </template>
                </slot>
            </template>
            <template v-else>
                <slot name="empty">
                    <template v-if="allowEmpty">
                        <span>{{emptyLabel|tr}}</span>
                    </template>
                    <template v-else>
                        <span v-tr>No selected {{itemName|tr}}|Aucun {{itemName|tr}} sélectionnés</span>
                    </template>
                </slot>
            </template>
        </div>

        <i class="fas ml-auto" :class="dropdownIcon"/>

        <b-modal v-if="modalActive" v-model="modalVisible" :title="tr(title)" :dialog-class="{'fullscreen': isMobileLayout}" :scrollable="isMobileLayout" body-class="p-0" @hidden="search = ''">
            <div class="bg-light p-2">
                <e-search-input v-model="search" autofocus :placeholder="searchPlaceholder" @input="resetHover" @enter="onEnter" @keypress="onKeyPress"/>
            </div>
            <slot name="filters"></slot>
            <b-list-group class="option-list compact interactive border-0">
                <b-list-group-item v-if="allowEmpty" @click.prevent="selectOption(emptyValue)" class="select-option"
                                   :class="selection === emptyValue ? `active bg-${variant}` : ''">

                    <e-check-mark :radio="!multiple" :value="selection === emptyValue"/>
                    <span class="option-label">
                        {{emptyLabel|tr}}
                    </span>
                </b-list-group-item>

                <b-list-group-item @click.prevent="selectOption(option)" v-for="option in optionsFiltered" :key="option[trackBy]"
                                   class="select-option"
                                   :class="[{'hover': option === hoverOption}, optionSelected(option) ? `active bg-${variant}` : '']">
                    <e-check-mark :radio="!multiple" :value="optionSelected(option)"/>
                    <span class="option-label">
                        <slot v-bind:option="option">{{ getItemLabel(option) }}</slot>

                        <template v-if="optionsFiltered && optionsFiltered.length < detailsThreshold">
                            <slot name="details" v-bind:option="option"></slot>
                        </template>
                    </span>
                </b-list-group-item>
            </b-list-group>

            <template #modal-footer>
                <b-badge variant="light" class="md" v-if="!isLive()">{{selection}}</b-badge>

                <b-btn v-if="allowEmpty" @click="setEmpty()" variant="danger">
                    {{tr(emptyButton)}}
                </b-btn>

                <b-btn class="ml-auto d-block" variant="primary" @click="modalVisible = false">
                    {{tr(okButton)}}
                </b-btn>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import {getNestedValue} from "../helpers/utils";
    import ECheckMark from "./e-check-mark.vue";
    import ESearchInput from "./e-search-input.vue";

    export default {
        name: `e-select-dialog`,
        components: {ECheckMark, ESearchInput},
        props: {
            value: { required: true },
            options: { type: Array, required: true },
            allowEmpty: { type: Boolean },
            emptyValue: { default: null },
            emptyLabel: { type: String, default: `None|Aucun` },
            itemName: { type: String, default: `items|éléments` },
            multiple: { type: Boolean, default: false },
            label: { type: String, default: `name` },
            trackBy: { type: String, default: `id` },
            searchKeys: {type: Array},
            dropdownIcon: {type: String, default: `fa-chevron-down`},
            autoHeight: {type: Boolean}, // automatically adjust input height to display selection details
            title: { type: String, default: `Select items|Sélection` },
            okButton: { type: String, default: `OK` },
            emptyButton: { type: String, default: `Unselect|Désélectionner` },
            detailsThreshold: { type: Number, default: 10 },
            searchPlaceholder: { type: String },
            // compact: first selected item + a badge counting the others, expanded: every selected item, the input grows to fit
            multipleDisplay: {type: String, default: `compact`, validator: value => [`compact`, `expanded`].includes(value)},
            badgeVariant: {type: String, default: `light`},
            variant: { type: String, default: `primary` }
        },
        data() {
            return {
                modalVisible: false,
                modalActive: false,
                hoverOption: null,
                search: ``
            }
        },
        computed: {
            selection: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit(`input`, value);
                }
            },
            optionsFiltered() {
                if (!this.search) {
                    return this.options;
                }

                let items = this.options;

                let matchingItems = [];
                let search = new RegExp(this.search, `i`);
                for (let item of items) {
                    let added = false;
                    for (let key of this.searchableKeys) {
                        let nestedValue = getNestedValue(item, key);
                        if (nestedValue !== undefined) {
                            if (typeof nestedValue === `object`) {
                                for (let k in nestedValue) {
                                    if (search.test(nestedValue[k])) {
                                        matchingItems.push(item);
                                        added = true;
                                        break;
                                    }
                                }
                            } else if (search.test(nestedValue)) {
                                added = true;
                                matchingItems.push(item);
                                break;
                            }
                        }
                        if (added) {
                            break;
                        }
                    }
                }
                return matchingItems;
            },
            isEmptySelected() {
                return this.emptyValue === this.selection;
            },
            singleOptionSelected() {
                if (!this.value) {
                    return null;
                } else if (!this.multiple) {
                    return this.options.find(o => o[this.trackBy] === this.value);
                } else {
                    return null;
                }
            },
            selectionOptionsDetails() {
                if (!this.options || !Array.isArray(this.selection)) {
                    return [];
                }
                // keep the selection order so the first badge is the first item picked
                return this.selection
                    .map(selectedId => this.options.find(o => o[this.trackBy] === selectedId))
                    .filter(option => option);
            },
            isExpandedSelection() {
                return this.multiple && this.multipleDisplay === `expanded`;
            },
            otherSelectionLabels() {
                return this.selectionOptionsDetails.slice(1).map(option => this.getItemLabel(option)).join(`, `);
            },
            searchableKeys() {
                if (this.searchKeys && this.searchKeys.length > 0) {
                    return this.searchKeys;
                } else {
                    return [this.trackBy, this.label];
                }
            }
        },
        methods: {
            showModal() {
                this.modalActive = true;
                this.modalVisible = true;
                this.resetHover();
            },
            selectOption(option) {
                if (option === this.emptyValue) {
                    this.selection = option;
                } else if (!this.multiple) {
                    this.selection = option[this.trackBy];
                } else {
                    const optionId = option[this.trackBy];
                    // always emit a new array so the parent receives an input event instead of a silent prop mutation
                    if (!Array.isArray(this.value) || this.isEmptySelected) {
                        this.selection = [optionId];
                    } else if (this.selection.indexOf(optionId) === -1) {
                        this.selection = [...this.selection, optionId];
                    } else {
                        this.selection = this.selection.filter(selectedId => selectedId !== optionId);
                    }
                }
            },
            optionSelected(option) {
                if (option === this.emptyValue) {
                    return option === this.selection;
                }
                if (!this.selection) {
                    return false;
                } else if (!this.multiple) {
                    return this.selection === option[this.trackBy];
                } else {
                    return this.selection.indexOf(option[this.trackBy]) !== -1;
                }
            },
            getItemLabel(item) {
                return item[this.label];
            },
            setEmpty() {
                this.modalVisible = false;
                this.selection = this.emptyValue;
            },
            onEnter() {
                if (this.optionsFiltered.length > 0) {
                    this.selectOption(this.optionsFiltered[0]);
                }
                this.modalVisible = false;
            },
            onKeyPress(event) {
                // console.log(event);
            },
            resetHover() {
                if (this.optionsFiltered?.length > 0) {
                    this.hoverOption = this.optionsFiltered[0];
                } else {
                    this.hoverOption = null;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .form-control {
        .flex-container {
            display: flex;
        }

        &.auto-height {
            min-height: 42px;
            height: auto;

            .flex-container {
                flex-direction: column;
            }
        }
    }

    .e-select-dialog {
        cursor: pointer;
        flex-basis: auto;
        flex-grow: 1;
        flex-shrink: 1;
        transition: background-color 200ms ease;
        display: flex;
        align-items: center;

        &:active {
            background-color: rgba(0, 0, 0, 0.1) !important;
        }

        .selection {
            min-width: 0;
        }

        .selection-badges {
            display: flex;
            align-items: center;
            max-width: 100%;

            .badge {
                margin-right: 4px;
                text-align: left;
            }

            .first-selection {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .other-selection-count {
                flex-shrink: 0;
            }
        }

        &.auto-height .selection-badges {
            flex-wrap: wrap;
            padding-top: 4px;

            .badge {
                margin-bottom: 4px;
            }
        }
    }

    .list-group {
        height: 500px;
        max-height: 80vh;
        overflow-y: auto;
        overflow-x: hidden;
        width: 100%;
    }

    .list-group-item {
        transition: background-color 250ms ease;
        font-weight: 500;
        cursor: pointer !important;

        &.select-option {
            display: flex;
            cursor: pointer;
            align-items: center;

            &.active {
                color: white;
            }

            .option-label {
                font-size: 10pt;
                padding-left: 10px;
                padding-right: 5px;
                width: 95%;
            }
        }
    }

    .e-search-input {
        ::v-deep .form-control {
            //border-radius: 0;
            padding-top: 15px;
            padding-bottom: 15px;
            height: 40px;
        }
    }
</style>
